import { Component, inject, OnInit, OnDestroy, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NotificationService } from '../../../views/notifications/services/notification.service';
import { SystemAlert } from '../../../views/notifications/models/Notifications';
import {
  systemAlertAcknowledgeClasses,
  systemAlertClasses,
  systemAlertIconPath,
} from '../../utils/system-alert-severity';

const POLL_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

@Component({
  selector: 'app-system-alert-banner',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './system-alert-banner.component.html',
  styleUrls: ['../../styles/markdown-content.css'],
})
export class SystemAlertBannerComponent implements OnInit, OnDestroy {
  private notificationService = inject(NotificationService);
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  alerts = signal<SystemAlert[]>([]);
  // Session only. Acknowledgement is what persists, and it lives on the server
  // keyed by user, so a dismissal can never leak between accounts on a shared
  // browser or be wiped by the localStorage reset on the login page.
  dismissedIds = signal<Set<number>>(new Set());
  acknowledgingIds = signal<Set<number>>(new Set());
  popupMinimized = signal(false);

  undismissedAlerts = computed(() => {
    const dismissed = this.dismissedIds();
    return this.alerts().filter((a) => !dismissed.has(a.id));
  });

  hasAlerts = computed(() => this.alerts().length > 0);
  hasUndismissed = computed(() => this.undismissedAlerts().length > 0);

  ngOnInit(): void {
    this.fetchAlerts();
    this.pollTimer = setInterval(() => this.fetchAlerts(), POLL_INTERVAL_MS);
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }

  /** Hides the alert until the next page load. Nothing is persisted. */
  dismiss(alertId: number): void {
    const updated = new Set(this.dismissedIds());
    updated.add(alertId);
    this.dismissedIds.set(updated);
  }

  /** Records the acknowledgement server side so the alert never returns. */
  acknowledge(alertId: number): void {
    if (this.acknowledgingIds().has(alertId)) {
      return;
    }
    this.setAcknowledging(alertId, true);

    this.notificationService.acknowledgeSystemAlert(alertId).subscribe({
      next: () => {
        this.setAcknowledging(alertId, false);
        this.alerts.set(this.alerts().filter((a) => a.id !== alertId));
        this.forgetDismissal(alertId);
      },
      error: () => {
        // Leave the alert on screen so the user can retry.
        this.setAcknowledging(alertId, false);
      },
    });
  }

  isAcknowledging(alertId: number): boolean {
    return this.acknowledgingIds().has(alertId);
  }

  togglePopup(): void {
    // the popup was minimized, clear dismissals so the popup can re-open with
    // the full list. Otherwise it acts as a normal hide toggle.
    if (this.popupMinimized() || !this.hasUndismissed()) {
      if (this.dismissedIds().size > 0) {
        this.dismissedIds.set(new Set());
      }
      this.popupMinimized.set(false);
    } else {
      this.popupMinimized.set(true);
    }
  }

  getAlertClasses(status: string): string {
    return systemAlertClasses(status);
  }

  getAcknowledgeClasses(status: string): string {
    return systemAlertAcknowledgeClasses(status);
  }

  getIconPath(status: string): string {
    return systemAlertIconPath(status);
  }

  private setAcknowledging(alertId: number, active: boolean): void {
    const updated = new Set(this.acknowledgingIds());
    if (active) {
      updated.add(alertId);
    } else {
      updated.delete(alertId);
    }
    this.acknowledgingIds.set(updated);
  }

  private forgetDismissal(alertId: number): void {
    if (!this.dismissedIds().has(alertId)) {
      return;
    }
    const updated = new Set(this.dismissedIds());
    updated.delete(alertId);
    this.dismissedIds.set(updated);
  }

  private fetchAlerts(): void {
    this.notificationService.getSystemAlerts().subscribe({
      next: (alerts: SystemAlert[]) => {
        this.alerts.set(alerts);
      },
    });
  }
}
